/**
 * 料金計算エンジン（Python 版 src/calculator.py の移植）。
 *
 * 仕様:
 * - 30分プランは、各30分コマの開始時刻に対応する時間帯の単価で積み上げる
 *   （17:30開始60分なら 昼1コマ＋夜1コマ）
 * - 「祝前」は日・祝料金。ただし「祝前日昼は平日料金」の注記があれば昼は平日料金
 * - ドリンク必須の場合、店舗のオプション行から金額を足せる（includeOptions）
 * - ワンオーダー額は店舗ごとに上書きできる（drinkCosts）
 *
 * 同じ入力で Python 版と同じ結果になることを golden ケースで検証する。
 */

import type { CalcParams, CalcResult, DayType, PlanRecord } from "./types";

const WEEKEND_DAYS = new Set<DayType>(["金", "土", "日・祝", "日・祝/土祝前", "祝前"]);

export function effectiveStart(startHour: number): number {
  return startHour < 6 ? startHour + 24 : startHour;
}

export function formatHour(value: number): string {
  const prefix = value >= 24 ? "翌" : "";
  let hour = Math.floor(value % 24);
  let minute = Math.round(((value % 24) - hour) * 60);
  if (minute === 60) {
    hour = (hour + 1) % 24;
    minute = 0;
  }
  return `${prefix}${hour}:${String(minute).padStart(2, "0")}`;
}

export function parseTimeStr(value: string): number | null {
  const match = /(\d{1,2})\s*[:：]\s*(\d{1,2})/.exec(value);
  if (!match) return null;
  return Number(match[1]) + Number(match[2]) / 60;
}

/** 曜日区分と時刻から単価を決める（注記による上書きを含む）。 */
function priceFor(plan: PlanRecord, dayType: DayType, hour: number): number | null {
  let price: number | null;
  switch (dayType) {
    case "月～木":
      price = plan.price.monThu;
      break;
    case "金":
      price = plan.price.fri;
      break;
    case "土":
      price = plan.price.sat;
      break;
    default:
      price = plan.price.sunHoliday;
  }

  const note = plan.note ?? "";
  if (dayType === "祝前" && hour < 18 && note.includes("祝前日昼は平日料金")) {
    price = plan.price.monThu;
  }
  if (dayType === "金" && hour >= 18 && note.includes("金・祝前18:00以降入室は¥")) {
    const match = /金・祝前18:00以降入室は[¥\s]*([\d,]+)/.exec(note);
    if (match) price = Number(match[1].replace(/,/g, ""));
  }
  return price;
}

interface Segment {
  plan: PlanRecord;
  start: number;
  end: number;
}

type PlanKey = string;

function planKey(plan: PlanRecord): PlanKey {
  return [plan.storeName, plan.planName, plan.usageType, plan.memberType, plan.audience].join("\u0000");
}

const AUDIENCE_BLOCK: Record<string, string[]> = {
  一般: ["学生", "中高生・高校生", "小中学生", "小学生", "20歳未満", "シニア"],
  学生: ["小学生", "小中学生", "シニア"],
  小中学生: ["シニア", "20歳未満"],
  シニア: ["学生", "中高生・高校生", "小中学生", "小学生", "20歳未満"],
};

class PricingContext {
  private readonly segments = new Map<PlanKey, Segment[]>();

  constructor(private readonly plans: PlanRecord[]) {
    for (const plan of plans) {
      if (plan.timeBand.start === null || plan.timeBand.end === null) continue;
      const key = planKey(plan);
      const list = this.segments.get(key) ?? [];
      list.push({ plan, start: plan.timeBand.start, end: plan.timeBand.end });
      this.segments.set(key, list);
    }
  }

  /** 指定時刻を含む時間帯の単価（境界は後ろの時間帯を優先）。 */
  segmentPrice(plan: PlanRecord, dayType: DayType, hour: number): number | null {
    const candidates = (this.segments.get(planKey(plan)) ?? []).filter(
      (segment) => segment.start <= hour && hour <= segment.end,
    );
    if (candidates.length === 0) return null;
    const latest = candidates.reduce((a, b) => (a.start >= b.start ? a : b));
    return priceFor(latest.plan, dayType, hour);
  }

  blockTotal(
    plan: PlanRecord,
    dayType: DayType,
    startHour: number,
    durationMinutes: number,
    fallbackUnit: number,
  ): number | null {
    const blocks = Math.ceil(durationMinutes / 30);
    if (plan.timeBand.start === null || plan.timeBand.end === null) {
      return fallbackUnit * blocks;
    }
    let total = 0;
    for (let index = 0; index < blocks; index += 1) {
      const price = this.segmentPrice(plan, dayType, startHour + index * 0.5);
      if (price === null) return null;
      total += price;
    }
    return total;
  }

  optionPrice(plan: PlanRecord, dayType: DayType, keywords: string[]): number | null {
    const candidates = [...this.plans].sort(
      (a, b) => (a.price.monThu ?? 10_000) - (b.price.monThu ?? 10_000),
    );
    for (const option of candidates) {
      if (option.storeName !== plan.storeName) continue;
      if (option.usageType !== "オプション" && option.usageType !== "利用時間内") continue;
      if (["学生", "まふ", "キッズ"].some((word) => option.planName.includes(word))) continue;
      if (option.audience !== "全員共通" && option.audience !== "一般") continue;
      if (!keywords.some((word) => option.planName.includes(word))) continue;
      const price = priceFor(option, dayType, 12);
      if (price !== null) return price;
    }
    return null;
  }
}

/** 条件に合うプランを計算し、安い順に返す。 */
export function calculate(plans: PlanRecord[], params: CalcParams): CalcResult[] {
  const startHour = parseTimeStr(params.startTime) ?? 14;
  const chkHour = effectiveStart(startHour);
  const stayEnd = chkHour + params.durationMinutes / 60;
  const context = new PricingContext(plans);
  const results: CalcResult[] = [];

  for (const plan of plans) {
    if (params.area && params.area !== "全エリア" && plan.area !== params.area) continue;

    const priceUnit = priceFor(plan, params.dayType, chkHour);
    if (priceUnit === null) continue;

    if (plan.usageType === "オプション" || plan.usageType === "利用時間内") continue;

    if (params.numPeople === 1) {
      if (plan.audience === "2名以上") continue;
    } else if (plan.audience.includes("ひとりカラオケ")) {
      continue;
    }

    const blocked = AUDIENCE_BLOCK[params.audience] ?? [];
    if (blocked.includes(plan.audience)) continue;

    if (!params.isMember && (plan.memberType === "会員限定" || plan.memberType === "会員")) {
      continue;
    }

    // ドリンク必須の扱い。includeOptions なら足りない分をオプションから合成する。
    let optionExtra = 0;
    if (params.drinkPref === "ソフトドリンクバー") {
      if (plan.drinkCondition !== "ソフトドリンクバー込" && plan.drinkCondition !== "ソフトドリンク飲み放題込") {
        if (!params.includeOptions) continue;
        const price = context.optionPrice(plan, params.dayType, ["ドリンクバー"]);
        if (price === null) continue;
        optionExtra = price;
      }
    } else if (params.drinkPref === "アルコール飲み放題") {
      if (plan.drinkCondition !== "アルコール飲み放題込") {
        if (!params.includeOptions) continue;
        const price = context.optionPrice(plan, params.dayType, ["飲み放題", "プレミアム", "アルコール"]);
        if (price === null) continue;
        optionExtra = price;
      }
    }

    const cond = plan.conditions;
    if (cond.receptionUntil !== null && chkHour > cond.receptionUntil) continue;
    if (cond.entryUntil !== null && chkHour > cond.entryUntil) continue;
    if (cond.weekendStart !== null && WEEKEND_DAYS.has(params.dayType) && chkHour < cond.weekendStart) {
      continue;
    }
    if (cond.minPeople > params.numPeople) continue;

    const warnings: string[] = [];
    if (plan.timeBand.start !== null && plan.timeBand.end !== null) {
      const { start, end } = plan.timeBand;
      if (!(start <= chkHour && chkHour <= end)) continue;
      if (stayEnd > end + 1e-9 && !plan.usageType.includes("30分")) {
        warnings.push(`${formatHour(end)}以降は利用できない可能性があります`);
      }
    }

    let planPrice = 0;
    let actualDuration = params.durationMinutes;

    if (plan.usageType.includes("30分")) {
      const total = context.blockTotal(plan, params.dayType, chkHour, params.durationMinutes, priceUnit);
      if (total === null) continue;
      planPrice = total;
    } else if (plan.usageType.includes("2時間")) {
      if (params.durationMinutes > 120) continue;
      planPrice = priceUnit;
      actualDuration = 120;
    } else if (plan.usageType.includes("3時間")) {
      if (params.durationMinutes > 180) continue;
      planPrice = priceUnit;
      actualDuration = 180;
    } else if (plan.usageType.includes("4時間")) {
      if (params.durationMinutes > 240) continue;
      planPrice = priceUnit;
      actualDuration = 240;
    } else if (plan.usageType.includes("フリータイム")) {
      if (cond.freeTimeUntil !== null && stayEnd > cond.freeTimeUntil) continue;
      planPrice = priceUnit;
    } else {
      continue;
    }

    let oneOrder = 0;
    if (plan.drinkCondition.includes("ワンオーダー制")) {
      oneOrder = params.drinkCosts?.[plan.storeName] ?? params.oneOrderDrinkCost;
    }
    if (optionExtra > 0) oneOrder = 0;
    const drinkExtra = oneOrder + optionExtra;

    if (params.numPeople === 1 && cond.soloPriceDiff) {
      warnings.push("1名利用は料金が異なる場合があります");
    }

    const totalPerPerson = planPrice + drinkExtra;
    const totalGroup = totalPerPerson * params.numPeople;

    const noteParts: string[] = [];
    if (oneOrder > 0) {
      noteParts.push(
        `室料¥${planPrice.toLocaleString("ja-JP")} + ワンオーダー目安¥${oneOrder.toLocaleString("ja-JP")}`,
      );
    }
    if (optionExtra > 0) {
      noteParts.push(`ドリンク代 ¥${optionExtra.toLocaleString("ja-JP")}（オプション）`);
    }
    if (plan.note) noteParts.push(plan.note);

    results.push({
      storeName: plan.storeName,
      area: plan.area,
      planName: plan.planName,
      usageType: plan.usageType,
      drinkCondition: plan.drinkCondition,
      memberType: plan.memberType,
      targetAudience: plan.audience,
      basePricePerPerson: planPrice,
      drinkExtraPerPerson: drinkExtra,
      totalPerPerson,
      totalGroup,
      numPeople: params.numPeople,
      durationMinutes: actualDuration,
      breakdownNote: noteParts.join(" / "),
      warnings,
    });
  }

  results.sort((a, b) => a.totalGroup - b.totalGroup || a.totalPerPerson - b.totalPerPerson);
  return results;
}

/** 店舗ごとに最も安いプランだけを残す。 */
export function cheapestByStore(results: CalcResult[]): CalcResult[] {
  const seen = new Set<string>();
  const cheapest: CalcResult[] = [];
  for (const result of results) {
    if (seen.has(result.storeName)) continue;
    seen.add(result.storeName);
    cheapest.push(result);
  }
  return cheapest;
}
